import AddIcon from '@mui/icons-material/Add';
import { Grid, Button, Box } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  candidateAggregatedData,
  ICalendarAccount,
  IOutlookCalendarAccount,
  ITaskSearchParam,
} from 'wisestep-api';
import {
  Item,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  ScrollableTabs,
  Spinner,
  WsTaskCard,
  WsDialog,
} from 'wisestep-base';

import { WsTaskCreationCard } from '../../../components/WsTaskCreationCard';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import useTaskSearch from '../../tasks/hooks/useTaskSearch';
import { closeTask } from '../../tasks/utils/TaskHelper';

import DashboardGrid from './DashboardGrid';

export interface encodedEmailType {
  candidateEmail?: string;
}

interface ITaskBoardParams {
  customQuery?: ITaskSearchParam;
  calendarEventAccountGoogle?: ICalendarAccount;
  calendarEventAccountOutlook?: IOutlookCalendarAccount;
}

const TaskBoard = ({
  customQuery,
  calendarEventAccountGoogle,
  calendarEventAccountOutlook,
}: ITaskBoardParams) => {
  const navigate = useNavigate();
  const taskList = [
    { tabName: 'All', tabCount: 0 },
    { tabName: 'Upcoming', tabCount: 0 },
    { tabName: 'Overdue', tabCount: 0 },
    { tabName: 'Closed', tabCount: 0 },
  ];
  const generateTaskQuery = (status: string, customQuery?: ITaskSearchParam) => {
    return {
      searchType: 'team',
      status: status,
      dueFromDateTime: null,
      dueTillDateTime: null,
      createdFromDateTime: null,
      createdTillDateTime: null,
      updatedFromDateTime: null,
      updatedTillDateTime: null,
      createdBy: [],
      team: [],
      sort: 'due_date',
      sortDirection: 'DESC',
      size: 50,
      page: 0,
      keywords: '',
      ...(customQuery &&
        customQuery.taskAttributes?.jobCode && {
          taskAttributes: { jobCode: customQuery.taskAttributes.jobCode },
        }),
    };
  };
  const [query, setQuery] = useState<ITaskSearchParam>(generateTaskQuery('ALL', customQuery));
  const [loader, setLoader] = useState<boolean>(false);
  const [createTask, setCreateTask] = useState<boolean>(false);
  const [tasks, taskCount] = useTaskSearch(query, setLoader);
  const openModal = useConversationWidgetStore((state) => state.updateId);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const taskFilter = (index: number) => {
    switch (index) {
      case 0:
        setQuery(generateTaskQuery('ALL', customQuery));
        break;
      case 1:
        setQuery(generateTaskQuery('UPCOMING', customQuery));
        break;
      case 2:
        setQuery(generateTaskQuery('OVERDUE', customQuery));
        break;
      case 3:
        setQuery(generateTaskQuery('CLOSED', customQuery));
        break;
    }
  };

  const openJobCRM = (id: string, candidateEmail: string) =>
    openUrlInNewTab(`/jobs-crm/${id}/false/0/pipeline/${candidateEmail}`);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(-1);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const onCompleteTask = async (taskId: string) => {
    setNotification(await closeTask(taskId));
    setQuery({ ...query });
  };

  return (
    <DashboardGrid container item xs={12} bgcolor={WsColors.White} sx={{ height: 600 }}>
      <Grid item md={8.5} sm={7}>
        <Item>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} bold display="inline">
            Reminders
          </WsTypography>
          <Box ml={3} display="inline">
            <WsTypography
              color={WsTypoColors.PRIMARY}
              bold
              display="inline"
              cursor="pointer"
              onClick={() => navigate('/tasks')}
            >
              View All
            </WsTypography>
          </Box>
        </Item>
      </Grid>
      <Grid item md={3.5} sm={5}>
        <Item sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateTask(true)}>
            Add Reminder
          </Button>
        </Item>
      </Grid>
      <Grid item xs={12} sx={{ height: 500 }}>
        <ScrollableTabs scrollList={taskList} defaultValue={0} getTabIndex={taskFilter} />
        {!loader ? (
          <>
            {taskCount > 0 ? (
              <Grid sx={{ overflowY: 'auto', height: 440 }}>
                {tasks?.map((task, index) => (
                  <Item sx={{ pb: 0 }} key={index}>
                    <WsTaskCard
                      taskStatus={task.status}
                      onCompleteTask={() => onCompleteTask(task.id)}
                      title={task.title ? task.title : ''}
                      description={
                        task.attributes &&
                        `${task.recruiterName ?? ''} has a task about 
                    ${task.attributes.jobTitle ?? ''} with 
                    ${task.attributes.candidateName ?? ''}`
                      }
                      date={task.dueDate ? task.dueDate.toString() : task?.modifiedOn.toString()}
                      jobRole={
                        task.attributes && task.attributes?.jobTitle
                          ? `${task.attributes?.jobTitle},${task.attributes.jobCode}`
                          : ''
                      }
                      jobId={(task.attributes && task.attributes.jobId) ?? ''}
                      checkBoxNeeded={false}
                      createdBy={task.recruiterName ?? ''}
                      createdByImgUrl={task.createdBy}
                      activeUsers={[]}
                      onJobTitleClick={() => {
                        openJobCRM(
                          task?.attributes?.jobId ?? '',
                          task.attributes?.candidateEmail ?? ''
                        );
                      }}
                      taskType={task.entityType}
                      onCandidateNameClick={async () => {
                        if (task.entityType === 'interaction' || task.entityType === 'candidate') {
                          let candidateInteractionId = task.entityId;
                          if (task.entityType === 'candidate') {
                            const candidateData = await candidateAggregatedData(
                              {
                                email: task.attributes?.candidateEmail ?? '',
                                candidateProfileURLs: [],
                                linkedInURL: '',
                              },
                              null,
                              1
                            );
                            if (candidateData.data?.conversations.length) {
                              candidateInteractionId = candidateData.data?.conversations[0].id;
                            }
                          }
                          openModal(candidateInteractionId);
                        }
                      }}
                      onSnoozeClick={() => {
                        setSelectedTaskIndex(index);
                        setOpenDialog(!openDialog);
                      }}
                      taskDescription={task.description ?? ''}
                      onEditClick={() => {
                        setSelectedTaskIndex(index);
                        setOpenDialog(!openDialog);
                      }}
                    />
                  </Item>
                ))}
              </Grid>
            ) : (
              <Grid mt={25} container justifyContent="center">
                <WsTypography>No reminder available</WsTypography>
              </Grid>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </Grid>
      <WsDialog size="large" openDialog={openDialog}>
        <WsTaskCreationCard
          updateTaskProp={true}
          taskDetails={tasks[selectedTaskIndex]}
          closeDialog={() => setOpenDialog(!openDialog)}
        />
      </WsDialog>
      <WsDialog openDialog={createTask}>
        <WsTaskCreationCard
          closeDialog={() => setCreateTask(false)}
          showHeader={true}
          calendarEventAccountGoogle={calendarEventAccountGoogle}
          calendarEventAccountOutlook={calendarEventAccountOutlook}
          loadCalendarFromParent
        />
      </WsDialog>
    </DashboardGrid>
  );
};

export default TaskBoard;
